/*
 * Empty-State-Hint Renderer fuer Overlay-Editoren (Memory + Personal-Dict).
 *
 * Beide Editoren rendern einen gedimmten "Geist-Layer", wenn das Modell leer
 * ist. Der Hint startet ZWEI Zeilen unter dem Pane-Top (offsetY + 2) — sonst
 * kollidiert er mit dem Cursor in Zeile 0.
 *
 * Proportional-Modus: nutzt `renderer.renderLabel` + `renderer.labelGridWidth`
 * fuer echten proportionalen Word-Wrap. Mono-Modus: setCharFn pro Glyph.
 */

import { Style } from '../config.js'

const HINT_OFFSET_Y = 2

/**
 * Rendert einen Empty-State-Hint mit Word-Wrap. Im Proportional-Modus
 * proportional, sonst monospace.
 *
 * @param {string} text - Hint-Text
 * @param {number} offsetX - Pane-Inner-Bounds-X
 * @param {number} offsetY - Pane-Inner-Bounds-Y (Hint startet bei offsetY + 2)
 * @param {Function} setCharFn - Mono-Fallback (x, y, ch, style)
 * @param {number} width - Pane-Breite in Grid-Spalten
 * @param {object} [renderer] - Renderer-Instanz; ohne wird Mono gerendert
 */
export function renderEmptyHint(text, offsetX, offsetY, setCharFn, width, renderer) {
    const startY = offsetY + HINT_OFFSET_Y
    const isProp = !!(renderer
        && typeof renderer.isProportional === 'function'
        && renderer.isProportional()
        && typeof renderer.renderLabel === 'function'
        && typeof renderer.labelGridWidth === 'function')

    const fits = (s) => isProp
        ? renderer.labelGridWidth(s) <= width
        : s.length <= width

    const flush = (line, y) => {
        if (!line) return
        if (isProp) {
            renderer.renderLabel(line, offsetX, y, Style.EDITOR_PLACEHOLDER, false, width)
        } else {
            const len = Math.min(line.length, width)
            for (let i = 0; i < len; i++) {
                setCharFn(offsetX + i, y, line[i], Style.EDITOR_PLACEHOLDER)
            }
        }
    }

    const words = text.split(/\s+/)
    let line = ''
    let y = startY
    for (const w of words) {
        const trial = line.length === 0 ? w : line + ' ' + w
        if (fits(trial)) {
            line = trial
        } else {
            flush(line, y)
            y++
            line = w
        }
    }
    flush(line, y)
}
